import {
  type ButtonHTMLAttributes,
  cloneElement,
  type ReactElement,
  type Ref,
} from 'react'
import './button.css'

/**
 * The one button.
 *
 * It lives in its own module rather than in `index`, because `index` re-exports
 * `Table`, and `Table`'s header renders buttons — importing Button back out of
 * the barrel would close a cycle. Harmless while every use is inside a
 * component, and then not harmless at all the first time something reads it at
 * module scope. A leaf module cannot participate in a cycle, so the question
 * stops arising.
 *
 * Variants are props, never new components: `<Button variant="quiet">`, never
 * `<QuietButton>`.
 *
 * - **default** — a solid ink fill. The one confident mark on a screen; one per
 *   view.
 * - **secondary** — a hairline-bordered neutral.
 * - **quiet** — the ghost, for tertiary and icon actions.
 * - **danger** — destruction and real failure, as `--danger` TEXT rather than a
 *   red fill. A filled red button competes with the primary for the eye and
 *   reads as the thing to press; deleting should be findable, not inviting.
 */
type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** The button itself, for a caller that hands focus back to it. */
  ref?: Ref<HTMLButtonElement>
  variant?: 'default' | 'quiet' | 'secondary' | 'danger'
  /** Renders as a pressed toggle. Also sets aria-pressed. */
  on?: boolean
  /** Square icon-only button; pass an accessible `aria-label`. */
  icon?: boolean
  /**
   * Spans its column, at the large control height, and wraps a long label
   * rather than pushing past the column — the commit that ends a narrow
   * surface and names a figure.
   */
  block?: boolean
  /**
   * `lg` is the published page's: the one action on a page read from further
   * off than a back office's toolbar. It wraps rather than overflowing a
   * narrow column, since its label can carry a name of any length. `sm` sits
   * inside a line of text — a notice's own way on — without growing the line.
   */
  size?: 'sm' | 'md' | 'lg'
  /**
   * The element to draw in place of the `<button>`, wearing its classes and
   * holding its children — an anchor where pressing it goes somewhere, since
   * an address has to stay a link to open in a tab, be copied and be followed
   * in place.
   */
  render?: ReactElement<{ className?: string; children?: unknown }>
}

export function Button({
  variant = 'default',
  on,
  icon,
  block,
  size = 'md',
  render,
  className,
  children,
  ...rest
}: ButtonProps) {
  const cls = [
    'btn',
    variant === 'quiet' && 'btn--quiet',
    variant === 'secondary' && 'btn--secondary',
    variant === 'danger' && 'btn--danger',
    icon && 'btn--icon',
    block && 'btn--block',
    size === 'sm' && 'btn--sm',
    size === 'lg' && 'btn--lg',
    render && 'btn--link',
    // Appended, never substituted. Spreading `className` through `...rest` put
    // it AFTER the computed classes and silently replaced them, so a caller
    // adding one modifier lost the variant it was modifying.
    className,
  ]
    .filter(Boolean)
    .join(' ')
  if (render) return cloneElement(render, { className: cls, children })
  return (
    <button
      data-active={on ? '' : undefined}
      type="button"
      className={cls}
      aria-pressed={on === undefined ? undefined : on}
      {...rest}
    >
      {children}
    </button>
  )
}
